/**
 * Printable GST tax invoice for an AUDINEXA subscription invoice (read-only view).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Printer, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function SubscriptionInvoicePage() {
  const { invoiceId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    axios.get(`${API}/subscription/invoices/${invoiceId}`).then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not load invoice'));
  }, [invoiceId]);

  if (err) return <div className="p-8 text-sm text-rose-700" data-testid="sub-invoice-error">{err}</div>;
  if (!d) return <div className="p-8 text-sm text-slate-400">Loading…</div>;
  const inv = d.invoice;
  const inter = inv.is_interstate || (inv.igst_amount > 0 && !inv.cgst_amount);
  return (
    <div className="p-4 sm:p-6 max-w-3xl" data-testid="sub-invoice-page" data-readonly-safe="true">
      <div className="flex items-center justify-between mb-4 print:hidden">
        <Link to="/billing/my-subscription" className="inline-flex items-center gap-1 text-[12.5px] text-slate-600 hover:text-slate-900" data-testid="sub-invoice-back"><ArrowLeft size={14} /> My Subscription</Link>
        <button onClick={() => window.print()} data-testid="sub-invoice-print-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors">
          <Printer size={13} /> Print / Save as PDF
        </button>
      </div>
      <article className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 text-[12.5px] text-slate-800 print:border-0 print:p-0">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <div className="text-xl font-extrabold tracking-tight">{d.seller.name}</div>
            <div className="text-slate-500">{d.seller.email}</div>
            {d.seller.gstin && <div>GSTIN: <b data-testid="sub-invoice-seller-gstin">{d.seller.gstin}</b></div>}
            {d.seller.state_code && <div>State code: {d.seller.state_code}</div>}
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Tax invoice</div>
            <div className="text-base font-bold" data-testid="sub-invoice-no">{inv.invoice_no || inv.invoice_id}</div>
            <div>Date: {fmtDate(inv.issued_at || inv.created_at)}</div>
            <div className="capitalize" data-testid="sub-invoice-status">Status: {String(inv.status || '').replace(/_/g, ' ')}</div>
          </div>
        </header>
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-500 mb-1">Billed to</div>
            <div className="font-semibold" data-testid="sub-invoice-buyer">{d.buyer.name}</div>
            {d.buyer.address && <div className="text-slate-600">{d.buyer.address}</div>}
            {d.buyer.gstin && <div>GSTIN: <b>{d.buyer.gstin}</b></div>}
          </div>
          <div className="sm:text-right">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-500 mb-1">Place of supply</div>
            <div data-testid="sub-invoice-pos">{inv.place_of_supply || '—'}</div>
            <div className="text-slate-500">{inter ? 'Inter-state (IGST)' : 'Intra-state (CGST + SGST)'}</div>
          </div>
        </section>
        <table className="w-full mt-4" data-testid="sub-invoice-lines">
          <thead><tr className="text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="text-left py-2">Description</th><th className="text-right py-2">Amount</th></tr></thead>
          <tbody>
            {d.lines.map((li, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`sub-invoice-line-${li.code}`}>
                <td className="py-2">{li.description}</td>
                <td className={`py-2 text-right tabular-nums ${li.amount < 0 ? 'text-emerald-700' : ''}`}>{inr(li.amount)}</td>
              </tr>
            ))}
            <Row label="Taxable value" value={d.taxable_amount} bold />
            {inter
              ? <Row label={`IGST @ ${inv.gst_percent || 0}%`} value={inv.igst_amount ?? inv.gst_amount} testid="sub-invoice-igst" />
              : <>
                  <Row label={`CGST @ ${(inv.gst_percent || 0) / 2}%`} value={inv.cgst_amount} testid="sub-invoice-cgst" />
                  <Row label={`SGST @ ${(inv.gst_percent || 0) / 2}%`} value={inv.sgst_amount} testid="sub-invoice-sgst" />
                </>}
            <tr className="text-[14px] font-extrabold"><td className="pt-3">Total</td><td className="pt-3 text-right tabular-nums" data-testid="sub-invoice-total">{inr(inv.grand_total ?? inv.amount)}</td></tr>
          </tbody>
        </table>
        {inv.razorpay_payment_id && <p className="mt-4 text-[11px] text-slate-500">Paid via Razorpay · {inv.razorpay_payment_id} · {fmtDate(inv.paid_at)}</p>}
        <p className="mt-6 text-[10.5px] text-slate-400">This is a computer-generated invoice and does not require a signature.</p>
      </article>
    </div>
  );
}

function Row({ label, value, bold, testid }) {
  return (
    <tr className={bold ? 'font-semibold' : 'text-slate-600'}>
      <td className="pt-2">{label}</td>
      <td className="pt-2 text-right tabular-nums" data-testid={testid}>{inr(value)}</td>
    </tr>
  );
}
